import clsx from "clsx";
import { FC, Fragment, useMemo } from "react";

import { downloadJsonFile } from "../../../../../utils/file";
import { updateSection, useSectionSettingActions } from "../../../../../utils/section";

import { AdminSectionListItem } from "./AdminSectionListItem";

import { serverCloneSection, serverExportSection } from "api/api-server-actions";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { type SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { storeApi, useAppDispatch } from "store/index";
import { OrderedSectionType } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { ComponentEventProperties } from "utils/amplitude";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection, isTemplateSection, isUnlinkedSection } from "utils/publishing";
import { isCCMEnabled } from "utils/waffle-flags";

export type SectionWithLinkedSections = {
  section: OrderedSectionType;
  linkedSections: OrderedSectionType[];
};

type AdminSectionListPanelProps = {
  title: string;
  sections: OrderedSectionType[];
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  canEdit: boolean;
  isDisabled?: boolean;
};

export const AdminSectionListPanel: FC<AdminSectionListPanelProps> = ({
  title,
  sections,
  eventPrefix,
  eventProperties,
  canEdit,
  isDisabled = false,
}) => {
  const { handleSectionDelete } = useSectionSettingActions();
  const { sortByActiveTitles } = useIsArchived();
  const dispatch = useAppDispatch();
  const ccmEnabled = isCCMEnabled();

  const sectionList = useMemo((): SectionWithLinkedSections[] => {
    if (!ccmEnabled) {
      const sorted = [...sections].toSorted(sortByActiveTitles);
      return sorted.map((section) => ({ section, linkedSections: [] }));
    }

    const templateAndUnlinkedSections = sections.filter(
      (section) => isTemplateSection(section) || isUnlinkedSection(section),
    );
    const linkedSections = sections.filter((section) => isLinkedSection(section));
    const linkedSectionsByTemplateId = new Map<string, OrderedSectionType[]>();

    for (const section of linkedSections) {
      if (section.template_id) {
        const list = linkedSectionsByTemplateId.get(section.template_id) ?? [];
        list.push(section);
        linkedSectionsByTemplateId.set(section.template_id, list);
      }
    }

    return templateAndUnlinkedSections
      .map((section) => ({
        section,
        linkedSections: (linkedSectionsByTemplateId.get(section.id) ?? []).toSorted(
          sortByActiveTitles,
        ),
      }))
      .toSorted((a, b) => sortByActiveTitles(a.section, b.section));
  }, [sections, sortByActiveTitles, ccmEnabled]);

  const handleClone = async (
    sectionId: string,
    courseId?: string,
    termId?: string,
    asTemplate?: boolean,
  ) => {
    try {
      await serverCloneSection(sectionId, courseId, termId, asTemplate);

      toastSuccessMessage(t("success.toasts.clone_section_started_success"));
    } catch (error) {
      toastLocalizedOperationCatcher("copy_section_failure")(error);
    }
  };

  const handleSectionSettings = (sectionId: string) => {
    dispatch(viewStateActions.setSectionSettingsForSectionId(sectionId));
  };

  const handleExportSection = (section: SectionType) => async () => {
    const sectionJson = await serverExportSection(section.id);
    downloadJsonFile(sectionJson, `${section.title}.json`);
  };

  const handleMoveTo = (section: SectionType) => (courseId: string, termId: string) => {
    updateSection({
      id: section.id,
      course_id: courseId,
      term_id: termId,
    });
  };

  const handleToggleArchive = (section: SectionType) => () => {
    storeApi.sections.partial_update({
      id: section.id,
      archived_at: section.archived_at ? null : new Date().toISOString(),
    });
  };

  return (
    <div className="mb-6 border-b border-black-tint-90" role="list">
      <HoverableToolbar reference="section">
        <h3 className={clsx("h3 my-2", isDisabled ? "text-black-tint-55" : "text-black-tint-20")}>
          {title}
        </h3>
      </HoverableToolbar>

      {sectionList.map((sectionWithLinkedSections) => (
        <Fragment key={sectionWithLinkedSections.section.id}>
          <AdminSectionListItem
            canEdit={canEdit}
            eventPrefix={eventPrefix}
            eventProperties={eventProperties}
            linkedSectionsCount={sectionWithLinkedSections.linkedSections.length}
            optionsMenuActions={{
              onSettings: () => handleSectionSettings(sectionWithLinkedSections.section.id),
              onDelete: () => handleSectionDelete(sectionWithLinkedSections.section),
              onDuplicate: () => handleClone(sectionWithLinkedSections.section.id),
              onDuplicateAsTemplate: () =>
                handleClone(sectionWithLinkedSections.section.id, undefined, undefined, true),
              onCopyTo: handleClone,
              onMoveTo: handleMoveTo(sectionWithLinkedSections.section),
              onExport: handleExportSection(sectionWithLinkedSections.section),
              onArchive: handleToggleArchive(sectionWithLinkedSections.section),
              onUnarchive: handleToggleArchive(sectionWithLinkedSections.section),
            }}
            section={sectionWithLinkedSections.section}
          />
          {sectionWithLinkedSections.linkedSections.map((linkedSection) => (
            <AdminSectionListItem
              canEdit={canEdit}
              className="ml-8"
              eventPrefix={eventPrefix}
              eventProperties={eventProperties}
              key={linkedSection.id}
              optionsMenuActions={{
                onSettings: () => handleSectionSettings(linkedSection.id),
                onDelete: () => handleSectionDelete(linkedSection),
                onDuplicate: () => handleClone(linkedSection.id),
                onCopyTo: handleClone,
                onMoveTo: handleMoveTo(linkedSection),
                onExport: handleExportSection(linkedSection),
                onArchive: handleToggleArchive(linkedSection),
                onUnarchive: handleToggleArchive(linkedSection),
              }}
              section={linkedSection}
            />
          ))}
          {ccmEnabled &&
            isTemplateSection(sectionWithLinkedSections.section) &&
            sectionWithLinkedSections.linkedSections.length === 0 && (
              <div className="body-s ml-8 flex min-h-[45px] items-center gap-2 border-t border-black-tint-90 px-3 text-black-tint-55">
                <span>{t("section_list_panel.no_linked_sections")}</span>
              </div>
            )}
        </Fragment>
      ))}

      {!canEdit && sectionList.length === 0 && (
        <div className="body-s ml-2 w-full py-2 text-black-tint-40">
          {t("section_list_panel.no_sections")}
        </div>
      )}
    </div>
  );
};
